import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight, Sparkles, Download, Share2, FileText, Check, Copy } from "lucide-react";

const GENRES = ["Drama", "Comedy", "Thriller", "Sci-Fi", "Horror", "Romance", "Action", "Mystery", "Documentary", "Fantasy"];
const FORMATS = ["Feature Film", "Short Film", "TV Series", "Web Series", "Commercial", "Music Video"];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openNew, setOpenNew] = React.useState(params.get("new") === "1");
  const [form, setForm] = React.useState({ title: "", logline: "", genre: "Drama", format: "Feature Film" });
  const [creating, setCreating] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error("Could not load projects");
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project created");
      setOpenNew(false);
      setForm({ title: "", logline: "", genre: "Drama", format: "Feature Film" });
      setParams({});
      window.location.href = `/app/projects/${data.id}/script`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create project");
    } finally {
      setCreating(false);
    }
  };

  const seedDemo = async () => {
    try {
      const { data } = await api.post("/seed-demo");
      toast.success("Demo project ready");
      window.location.href = `/app/projects/${data.project_id}/script`;
    } catch (e) { toast.error("Could not seed demo"); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Projects</div>
            <h1 className="font-editorial text-4xl">Your stories</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={seedDemo} className="border border-[#27272A] hover:border-[#A1A1AA] px-4 py-2 text-sm inline-flex items-center gap-2 transition-colors" data-testid="seed-demo-btn">
              <Sparkles size={14} className="text-[#D4AF37]" /> Load demo
            </button>
            <button onClick={() => setOpenNew(true)} className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium hover:bg-[#F59E0B] inline-flex items-center gap-2 transition-colors" data-testid="new-project-btn">
              <Plus size={14} /> New project
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-[#141414] border border-[#1E1E1E] animate-pulse"/>)}
          </div>
        ) : projects.length === 0 ? (
          <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
            <div className="font-editorial text-2xl">Your first project awaits.</div>
            <div className="text-sm text-[#A1A1AA] mt-2">Create a new project or load the demo screenplay to explore.</div>
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => setOpenNew(true)} className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium" data-testid="empty-new-project">
                Create project
              </button>
              <button onClick={seedDemo} className="border border-[#27272A] px-5 py-2 text-sm">Load demo</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} onRefresh={load} />
            ))}
          </div>
        )}
      </div>

      {openNew && (
        <div className="fixed inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setOpenNew(false)}>
          <div className="w-full max-w-lg bg-[#141414] border border-[#27272A] p-8" onClick={(e) => e.stopPropagation()} data-testid="new-project-modal">
            <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-3">New Project</div>
            <h2 className="font-editorial text-3xl mb-6">A new story begins.</h2>
            <form onSubmit={create} className="space-y-4">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testId="new-project-title" />
              <Field label="Logline" value={form.logline} onChange={(v) => setForm({ ...form, logline: v })} multiline testId="new-project-logline" />
              <div className="grid grid-cols-2 gap-4">
                <SelectField label="Genre" value={form.genre} onChange={(v) => setForm({ ...form, genre: v })} options={GENRES} testId="new-project-genre" />
                <SelectField label="Format" value={form.format} onChange={(v) => setForm({ ...form, format: v })} options={FORMATS} testId="new-project-format" />
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setOpenNew(false)} className="text-sm text-[#A1A1AA] hover:text-[#FFFFF0] px-4 py-2">Cancel</button>
                <button type="submit" disabled={creating || !form.title.trim()}
                  className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="new-project-submit">
                  {creating ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, multiline, required, testId }) {
  const props = {
    value, onChange: (e) => onChange(e.target.value), required,
    className: "w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-[#FFFFF0] px-4 py-2.5 text-sm placeholder-[#71717A]",
    "data-testid": testId,
  };
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">{label}</div>
      {multiline ? <textarea rows={3} {...props} /> : <input type="text" {...props} />}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-[#FFFFF0] px-4 py-2.5 text-sm"
        data-testid={testId}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function ProjectCard({ p, onRefresh }) {
  const [busy, setBusy] = React.useState(null);
  const [publicSlug, setPublicSlug] = React.useState(p.slug && p.is_public ? p.slug : null);

  const downloadFountain = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy("fountain");
    try {
      const token = localStorage.getItem("sf_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/export.fountain`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.title.replace(/\s+/g, "_")}.fountain`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fountain file downloaded");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setBusy(null);
    }
  };

  const publish = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy("publish");
    try {
      const { data } = await api.post(`/projects/${p.id}/publish`);
      setPublicSlug(data.slug);
      toast.success("Published to community");
      onRefresh?.();
    } catch (err) {
      toast.error("Publish failed");
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}/story/${publicSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch (err) {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="group bg-[#141414] border border-[#1E1E1E] p-6 hover:border-[#D4AF37] transition-colors relative" data-testid={`project-card-${p.id}`}>
      <Link to={`/app/projects/${p.id}/script`} className="block">
        <div className="uppercase text-[10px] tracking-widest text-[#71717A] mb-3 flex items-center gap-2">
          <span>{p.genre || "—"} · {p.format}</span>
          {p.is_public && <span className="text-[#D4AF37]" data-testid={`project-badge-public-${p.id}`}>· PUBLIC</span>}
        </div>
        <div className="font-editorial text-2xl mb-3 line-clamp-2">{p.title}</div>
        <div className="text-sm text-[#A1A1AA] line-clamp-3 leading-relaxed min-h-[3.5rem]">
          {p.logline || "No logline yet — click to add."}
        </div>
      </Link>
      <div className="mt-4 pt-4 border-t border-[#1E1E1E] flex items-center gap-2 text-xs">
        <button onClick={downloadFountain} disabled={busy === "fountain"}
          className="inline-flex items-center gap-1 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          title="Export as Fountain (.fountain)"
          data-testid={`project-fountain-${p.id}`}>
          <FileText size={12} /> Fountain
        </button>
        {!publicSlug ? (
          <button onClick={publish} disabled={busy === "publish"}
            className="inline-flex items-center gap-1 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors ml-auto"
            title="Publish to community as read-only page"
            data-testid={`project-publish-${p.id}`}>
            <Share2 size={12} /> Publish
          </button>
        ) : (
          <button onClick={copyLink}
            className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F59E0B] transition-colors ml-auto"
            title="Copy public link"
            data-testid={`project-share-${p.id}`}>
            <Copy size={12} /> Copy link
          </button>
        )}
      </div>
    </div>
  );
}
